import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from '_common/hooks';
import { useParams } from 'react-router';
import { Dropzone, type DropzoneProps } from '@ets/design-system';

import { navigateToMyFiles } from 'router/history';

import { useEffectOnUpdate } from '_common/hooks';
import {
  EmptyFilteredState,
  ErrorView,
  FilterDisplay,
  InformationPanel,
  InteractionController,
  IntlErrorBoundary,
  ObjectPreview,
  TableColumnsToggle,
} from '_common/components';
import { InstanceService } from '_common/services';
import { useGetUserSettingsQuery } from '_common/services/api/UserApi';
import { setCleanState, setCurrentAppPage } from 'App/redux/appSlice';
import { loadStorageData, uploadFile, toggleInfoPanel } from './StoragePageSlice';
import { updateMiniExplorer } from '_common/components/MiniExplorer/miniExplorerSlice';
import { listObjects } from '_common/components/Table/TableSlice';
import { setActiveTab, setTriggerPage } from 'Search/redux/SearchPageSlice';
import { useFilterSelector } from '_common/components/Filters/FilterSlice';
import { useOnboardingExplorerActive } from '_common/hooks/Onboarding';

import Header from './Header/Header';

import styles from './StoragePage.module.scss';

import StoragePageTable from './Table/Table';

const PAGE_IDENTITY = 'storage';

/**
 * Storage Page
 */
const StoragePage = () => {
  const dispatch = useDispatch();
  const params: { id: ObjectId; type: ObjectType } = useParams();
  const { selectFilterParams } = useFilterSelector();

  const data = useSelector((state) => state.app.data);
  const current = useSelector((state) => data[state.storage.current ?? '']);
  const breadcrumb = useSelector((state) => state.storage.breadcrumb);
  const error = useSelector((state) => state.app.error);
  const infoPanelOpen = useSelector((state) => state.storage.infoPanelOpen);
  const tableListLength = useSelector((state) => state.table.identity.storage.list.length);
  const onboardingExplorerIsActive = useOnboardingExplorerActive();
  const [loading, setLoading] = useState(true);
  const { data: userSettings } = useGetUserSettingsQuery();

  // const userId = useSelector((state) => state.auth.userId);
  const filterParams = useSelector((state) => selectFilterParams(state, state.filters.storage));
  const filters = useMemo(() => {
    return {
      ...JSON.parse(localStorage.getItem(PAGE_IDENTITY) ?? '{}'),
      ...filterParams,
    };
  }, [filterParams]);

  const [resetScroll, setResetScroll] = useState(false);

  useEffect(() => {
    if (resetScroll) {
      setResetScroll(false);
    }
  }, [resetScroll]);

  //#region Table object listing handlers
  const fetchObjects = useCallback(
    async (parameters: any) => {
      const instanceService = new InstanceService();

      let { id, type }: { id: ObjectId; type: ObjectType } = params;

      if (!id || !type) {
        const { data } = await instanceService.getPersonalSpaceInfo();
        //@ts-expect-error Missing endpoint type "/api/object/space/get"
        id = data.id;
        //@ts-expect-error Missing endpoint type "/api/object/space/get"
        type = data.type;
      }

      return instanceService.listObject(id, type, parameters);
    },
    [params.id, params.type],
  );

  useEffect(() => {
    dispatch(setCleanState());
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, [onboardingExplorerIsActive]);

  useEffectOnUpdate(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'FILTER',
        request: { offset: 0, ...filters },
      }),
    );
  }, [fetchObjects, filters]);
  //#endregion

  useEffect(() => {
    dispatch(updateMiniExplorer({ identity: PAGE_IDENTITY }));
    return () => {
      dispatch(updateMiniExplorer({ identity: undefined }));
    };
  }, []);

  useEffect(() => {
    const { type } = params;

    dispatch(setTriggerPage(type ? `storage/${type}` : 'storage'));
    dispatch(setActiveTab('parent'));
  }, [params.type]);

  useEffect(() => {
    if (current) {
      if (current.type === 'folder') {
        // Current object is a folder and not a space root
        if (breadcrumb.parents.length > 0) {
          // Folder has parents that the user can access
          const root: {
            id: string;
            personal: boolean;
            type?: string;
          } = breadcrumb.parents[0];
          if (root.type === 'folder') {
            // Root object is not a space, means user has no access to the real root -> shared
            dispatch(setCurrentAppPage('/shared'));
          } else if (root.personal) {
            // Root is not a folder and also personal -> my files (beware superusers)
            dispatch(setCurrentAppPage('/storage'));
          } else {
            // Root is not a folder and not personal
            dispatch(setCurrentAppPage('/storage/spaces'));
          }
        } else {
          // User can't access folder parents -> shared
          dispatch(setCurrentAppPage('/shared'));
        }
      } else if (current.type === 'space' && current.personal) {
        // Current object is a personal space -> my files (beware superusers)
        dispatch(setCurrentAppPage('/storage'));
      } else {
        // Current is a space -> spaces
        dispatch(setCurrentAppPage('/storage/spaces'));
      }
    }
  }, [current]);

  useEffect(() => {
    const promise = dispatch(
      loadStorageData({
        id: params.id,
        type: params.type,
        parameters: {
          offset: 0,
          size: 200,
          ...filters,
        },
      }),
    );
    promise.then((resp) => {
      if (resp.meta.requestStatus !== 'rejected') {
        setLoading(false);
      }
    });

    return () => {
      promise?.abort();
    };
  }, [params.id, params.type]);

  const handleUploadFile: DropzoneProps['onDrop'] = (acceptedFiles) => {
    const parameterName = () => {
      if (params.type === 'space') {
        return 'space';
      } else return 'parent';
    };

    dispatch(
      uploadFile({
        identity: PAGE_IDENTITY,
        parameters: {
          files: [...acceptedFiles],
          description: '',
          [parameterName()]: params.id,
        },
      }),
    );

    setResetScroll(true);
  };

  const handleCloseInfoPanel = () => {
    dispatch(toggleInfoPanel());
  };

  const hasError = error.status === 400 || error.status === 403 || error.status === 404;

  // ----------------------------------------------------------
  // ----------------------- Renders -------------------------

  if (!current || !userSettings) {
    return null;
  }

  const renderContent = () => {
    if (hasError) {
      return <ErrorView error={error} onClick={navigateToMyFiles} />;
    }

    //If has filters and no matching objects to display
    if (filterParams.filter_fields.length > 0 && tableListLength === 0) {
      return <EmptyFilteredState identity="storage" />;
    }
    return (
      <div className={styles.content}>
        <Dropzone name="storage" onDrop={handleUploadFile} noClick testId="storage-page">
          <InteractionController environment="explorer" style={{ width: '100%', height: '100%' }}>
            <div className={styles.listView}>
              <StoragePageTable fetchObjects={fetchObjects} filters={filters} current={current} />
            </div>
          </InteractionController>
        </Dropzone>
        <InformationPanel
          isOpen={infoPanelOpen}
          onClose={handleCloseInfoPanel}
          testId="storage-information-panel"
        />
      </div>
    );
  };

  return (
    <>
      <div id="WelcomeScreen" />
      <div className={styles.root} id="ExplorerStoragePage">
        <div className={styles.header}>
          <IntlErrorBoundary size="s" style={{ margin: '0 0 0 7rem' }}>
            <Header
              hasError={hasError}
              loading={loading}
              setResetScroll={setResetScroll}
              infoPanelOpen={infoPanelOpen}
              handleCloseInfoPanel={handleCloseInfoPanel}
            />
          </IntlErrorBoundary>
        </div>
        <div className={styles.headerFooter}>
          <div style={{ flex: 1 }}>
            <FilterDisplay identity="storage" margin={'0 0 0 6rem'} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', userSelect: 'none' }}>
            <TableColumnsToggle table="explorer" />
          </div>
        </div>
        {renderContent()}
        <ObjectPreview />
      </div>
    </>
  );
};

export default StoragePage;
